import { useState } from 'react';
import {
  Badge,
  Button,
  Grid,
  GridCol,
  Group,
  NumberInput,
  Paper,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
  Textarea,
  Title,
} from '@mantine/core';
import { IconArrowLeft, IconDownload } from '@tabler/icons-react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from 'urql';
import { ErrorAlert } from '@/components/error-alert';
import { TemplateVariableEditor, readTemplateVariables } from '@/components/template-variable-editor';
import type { TemplateVariable } from '@/components/template-variable-editor';
import { getErrorMessage } from '@/lib/error-message';
import { ImportFlowSnapshotQuery, ImportFlowsQuery, ImportOrganizationsQuery } from './graphql';
import { readJsonArray } from './json-array';

export type FlowTemplateFormValues = {
  name: string;
  description: string;
  category: string;
  featured: boolean;
  sortOrder: number;
  variables: TemplateVariable[];
  flowName: string;
  flowDescription: string;
  nodes: unknown[];
  edges: unknown[];
  entryNodeId: string;
  voiceConfig: unknown;
};

export const emptyFlowTemplateValues: FlowTemplateFormValues = {
  name: '',
  description: '',
  category: '',
  featured: false,
  sortOrder: 0,
  variables: [],
  flowName: '',
  flowDescription: '',
  nodes: [],
  edges: [],
  entryNodeId: '',
  voiceConfig: null,
};

type FlowTemplateRecord = {
  name: string;
  description: string;
  category: string | null;
  featured: boolean;
  sortOrder: number;
  variables: unknown;
  flowName: string;
  flowDescription: string | null;
  nodes: unknown;
  edges: unknown;
  entryNodeId: string | null;
  voiceConfig: unknown;
};

export function toFlowTemplateValues(template: FlowTemplateRecord): FlowTemplateFormValues {
  return {
    name: template.name,
    description: template.description,
    category: template.category ?? '',
    featured: template.featured,
    sortOrder: template.sortOrder,
    variables: readTemplateVariables(template.variables),
    flowName: template.flowName,
    flowDescription: template.flowDescription ?? '',
    nodes: readJsonArray(template.nodes),
    edges: readJsonArray(template.edges),
    entryNodeId: template.entryNodeId ?? '',
    voiceConfig: template.voiceConfig,
  };
}

export function toFlowTemplateInput(values: FlowTemplateFormValues) {
  return {
    name: values.name.trim(),
    description: values.description.trim(),
    category: values.category.trim() || null,
    featured: values.featured,
    sortOrder: values.sortOrder,
    variables: values.variables,
    flowName: values.flowName.trim(),
    flowDescription: values.flowDescription.trim() || null,
    nodes: values.nodes,
    edges: values.edges,
    entryNodeId: values.entryNodeId || null,
    voiceConfig: values.voiceConfig,
  };
}

type FlowSnapshot = {
  name: string;
  description: string | null;
  nodes: unknown[];
  edges: unknown[];
  entryNodeId: string | null;
  voiceConfig: unknown;
};

function FlowImportPicker({ onImport }: { onImport: (snapshot: FlowSnapshot) => void }) {
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [flowId, setFlowId] = useState<string | null>(null);

  const [organizations] = useQuery({ query: ImportOrganizationsQuery });

  const [flows] = useQuery({
    query: ImportFlowsQuery,
    variables: { organizationId: organizationId ?? '' },
    pause: !organizationId,
  });

  const [snapshot] = useQuery({
    query: ImportFlowSnapshotQuery,
    variables: { id: flowId ?? '' },
    pause: !flowId,
  });

  const flow = snapshot.data?.flowsByPk ?? null;
  const loadError = organizations.error ?? flows.error ?? snapshot.error;

  return (
    <Paper p="sm" withBorder>
      <Stack gap="xs">
        <Text size="sm" fw={500}>
          Import from Existing Flow
        </Text>
        <ErrorAlert message={loadError ? getErrorMessage(loadError, 'Failed to load flows') : null} />
        <Group gap="xs" grow>
          <Select
            placeholder="Select organization"
            data={(organizations.data?.organizations ?? []).map((o) => ({ value: o.id, label: o.name }))}
            value={organizationId}
            onChange={(value) => {
              setOrganizationId(value);
              setFlowId(null);
            }}
            searchable
            clearable
            size="xs"
          />
          <Select
            placeholder="Select flow"
            data={(flows.data?.flows ?? []).map((f) => ({
              value: f.id,
              label: `${f.name} (${readJsonArray(f.nodes).length} nodes)`,
            }))}
            value={flowId}
            onChange={setFlowId}
            searchable
            clearable
            disabled={!organizationId}
            size="xs"
          />
          <Button
            size="xs"
            variant="light"
            leftSection={<IconDownload size={14} />}
            onClick={() => {
              if (!flow) return;

              onImport({
                name: flow.name,
                description: flow.description,
                nodes: readJsonArray(flow.nodes),
                edges: readJsonArray(flow.edges),
                entryNodeId: flow.entryNodeId,
                voiceConfig: flow.voiceConfig,
              });
            }}
            disabled={!flow}
            style={{ flexGrow: 0, flexShrink: 0 }}
          >
            Import
          </Button>
        </Group>
      </Stack>
    </Paper>
  );
}

type FlowTemplateFormProps = {
  initialValues: FlowTemplateFormValues;
  submitLabel: string;
  onSubmit: (values: FlowTemplateFormValues) => Promise<string | null>;
};

export function FlowTemplateForm({ initialValues, submitLabel, onSubmit }: FlowTemplateFormProps) {
  const navigate = useNavigate();
  const [values, setValues] = useState(initialValues);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const patchValues = (patch: Partial<FlowTemplateFormValues>) => setValues((current) => ({ ...current, ...patch }));

  const goToList = () => void navigate({ to: '/system/flow-templates' });

  const importFlow = (snapshot: FlowSnapshot) =>
    setValues((current) => ({
      ...current,
      nodes: snapshot.nodes,
      edges: snapshot.edges,
      entryNodeId: snapshot.entryNodeId ?? '',
      voiceConfig: snapshot.voiceConfig,
      flowName: current.flowName || snapshot.name,
      flowDescription: current.flowDescription || (snapshot.description ?? ''),
    }));

  const submit = async () => {
    setIsSubmitting(true);
    setFormError(null);

    const message = await onSubmit(values);

    setIsSubmitting(false);

    if (message) setFormError(message);
    else goToList();
  };

  return (
    <Stack gap="lg">
      <Group>
        <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} onClick={goToList}>
          Back
        </Button>
      </Group>

      <ErrorAlert message={formError} />

      <Grid gutter="xl">
        <GridCol span={{ base: 12, md: 5 }}>
          <Paper p="lg" withBorder>
            <Stack gap="md">
              <Title order={4}>Template Metadata</Title>
              <TextInput
                label="Template Name"
                placeholder="e.g. Basic IVR Menu"
                value={values.name}
                onChange={(e) => patchValues({ name: e.target.value })}
                required
              />
              <Textarea
                label="Template Description"
                placeholder="Describe what this template is for"
                value={values.description}
                onChange={(e) => patchValues({ description: e.target.value })}
                required
              />
              <Group grow>
                <TextInput
                  label="Category"
                  placeholder="e.g. IVR, Support"
                  value={values.category}
                  onChange={(e) => patchValues({ category: e.target.value })}
                />
                <NumberInput
                  label="Sort Order"
                  value={values.sortOrder}
                  onChange={(value) => patchValues({ sortOrder: typeof value === 'number' ? value : 0 })}
                />
              </Group>
              <Switch
                label="Featured"
                checked={values.featured}
                onChange={(e) => patchValues({ featured: e.currentTarget.checked })}
              />
            </Stack>
          </Paper>
        </GridCol>

        <GridCol span={{ base: 12, md: 7 }}>
          <Paper p="lg" withBorder>
            <Stack gap="md">
              <Title order={4}>Flow Content</Title>

              <FlowImportPicker onImport={importFlow} />

              <TextInput
                label="Flow Name"
                description="Use <<varKey>> for variable placeholders"
                placeholder="e.g. <<companyName>> Main Menu"
                value={values.flowName}
                onChange={(e) => patchValues({ flowName: e.target.value })}
                required
              />
              <Textarea
                label="Flow Description"
                placeholder="e.g. IVR flow for <<companyName>>"
                value={values.flowDescription}
                onChange={(e) => patchValues({ flowDescription: e.target.value })}
              />

              <Group gap="xs">
                <Badge size="sm" variant="light">
                  {values.nodes.length} nodes
                </Badge>
                <Badge size="sm" variant="light">
                  {values.edges.length} edges
                </Badge>
                {values.entryNodeId && (
                  <Badge size="sm" variant="light" color="green">
                    Entry node set
                  </Badge>
                )}
                {!!values.voiceConfig && (
                  <Badge size="sm" variant="light" color="blue">
                    Voice config set
                  </Badge>
                )}
              </Group>
            </Stack>
          </Paper>
        </GridCol>
      </Grid>

      <Paper p="lg" withBorder>
        <Stack gap="md">
          <Title order={4}>Variables</Title>
          <TemplateVariableEditor variables={values.variables} onChange={(variables) => patchValues({ variables })} />
        </Stack>
      </Paper>

      <Group justify="flex-end">
        <Button variant="subtle" onClick={goToList}>
          Cancel
        </Button>
        <Button
          onClick={() => void submit()}
          loading={isSubmitting}
          disabled={!values.name.trim() || !values.flowName.trim() || values.nodes.length === 0}
        >
          {submitLabel}
        </Button>
      </Group>
    </Stack>
  );
}
